import { Form } from 'antd';
import { FormField, FormTextArea, FormTitle } from '../form/FormBasic';
import { FormFieldTypeEnum } from '../../enums/FormFieldTypeEnum';
import { FormInstance, useWatch } from 'antd/es/form/Form';
import { useTranslation } from 'react-i18next';
import PrimaryButton from '../buttons/PrimaryButton';
import DefaultButton from '../buttons/DefaultButton';
import SecondaryButton from '../buttons/SecondaryButton';
import { useState } from 'react';
import {
  REGEX_ALL_ALLOWED_SAFE_FILTER,
  REGEX_LETTERS_SPACE_FULLSTOP_SAFE_FILTER,
} from '../../constants/regex';
import {
  maxLengthRule,
  minLengthRule,
  noSpacesOnlyRule,
  regexRule,
  requiredRule,
} from '../../helpers/formHelper';

import { IRespondent, RespondentFormProps } from '../../types/priceTypes';

const RespondentDetailForm: React.FC<RespondentFormProps> = ({
  form,
  handleSubmit,
  handleCancel,
  disabled,
  fetchLocationData,
}) => {
  const { t } = useTranslation('price');
  const [isLocationLoading, setIsLocationLoading] = useState(false);

  const respondentNameVal = useWatch('respondent', form);
  const respondentSignatureVal = useWatch('signatureInitial', form);

  const hasAllFieldsValue = !!respondentNameVal && !!respondentSignatureVal;
  return (
    <>
      <FormTitle
        label={t('completeRegistrationHeading')}
        styles="text-xl font-semibold text-black mb-4"
      />
      <Form form={form} name="respondentDataForm" layout="vertical" disabled={disabled}>
        {/* Respondent Name */}
        <FormField
          name="respondent"
          label={t('respondentName')}
          placeholder={t('RespondentFieldPlaceholder')}
          isRequired={true}
          rules={[
            requiredRule(t, t('respondentName')),
            noSpacesOnlyRule(t, t('respondentName')),
            minLengthRule(t, t('respondentName'), 3),
            maxLengthRule(t, t('respondentName'), 100),
            regexRule(t, REGEX_LETTERS_SPACE_FULLSTOP_SAFE_FILTER),
          ]}
          disabled={false}
          type={FormFieldTypeEnum.INPUT}
          allowLeadingTrailingSpaces={true}
          useCustomRules={true}
        />

        {/* Signature Initial */}
        <FormField
          name="signatureInitial"
          label={t('signatureOfRespondent')}
          placeholder={t('RespondentSignatureFieldPlaceholder')}
          isRequired={true}
          rules={[
            requiredRule(t, t('signatureOfRespondent')),
            noSpacesOnlyRule(t, t('signatureOfRespondent')),
            minLengthRule(t, t('signatureOfRespondent'), 1),
            maxLengthRule(t, t('signatureOfRespondent'), 100),
            regexRule(t, REGEX_LETTERS_SPACE_FULLSTOP_SAFE_FILTER),
          ]}
          disabled={false}
          type={FormFieldTypeEnum.INPUT}
          allowLeadingTrailingSpaces={true}
          useCustomRules={true}
        />

        {/* Remarks Input */}
        <FormTextArea
          name="finalRemarks"
          label={t('generalObservations')}
          placeholder={t('RespondentObservationsFieldPlaceholder')}
          isRequired={false}
          rules={[
            regexRule(t, REGEX_ALL_ALLOWED_SAFE_FILTER),
            noSpacesOnlyRule(t, t('generalObservations')),
            minLengthRule(t, t('generalObservations'), 3),
            maxLengthRule(t, t('generalObservations'), 200),
          ]}
          disabled={false}
          useCustomRules={true}
        />

        {/* Browser Geolocation */}
        {fetchLocationData && (
          <div className="m-4">
            {isLocationLoading ? (
              <div>{t('accessingLocation')}</div>
            ) : (
              <>
                <span className="">{t('locationDataNotFetched')}</span>
                <button
                  onClick={async () => {
                    setIsLocationLoading(true);
                    try {
                      await fetchLocationData();
                    } finally {
                      setIsLocationLoading(false);
                    }
                  }}
                  disabled={isLocationLoading}
                >
                  {t('clickHereToTryAgain')}
                </button>
              </>
            )}
          </div>
        )}
        {/* Footer Actions */}
        <div className="mt-6 flex items-center justify-between gap-6">
          <DefaultButton handleOnClick={handleCancel} width="100%" type="button">
            {t('cancel')}
          </DefaultButton>
          {hasAllFieldsValue ? (
            <PrimaryButton
              handleOnClick={(e) => {
                e.preventDefault();
                handleSubmit();
              }}
              width="100%"
              disabled={disabled}
              hideBgGradient={true}
            >
              {t('submit')}
            </PrimaryButton>
          ) : (
            <SecondaryButton
              handleOnClick={(e) => {
                e.preventDefault();
                handleSubmit();
              }}
              width="100%"
              disabled={!hasAllFieldsValue}
            >
              {t('submit')}
            </SecondaryButton>
          )}
        </div>
      </Form>
    </>
  );
};

export default RespondentDetailForm;
